// Форма отчёта о пропусках АИС: предпросмотр списка и окно до часа (UTC).
//
// Предпросмотр нужен, чтобы человек увидел опечатку ДО отправки; задание ставит
// разбор сервера (`src/ais/gap_report.py::parse_mmsi_list`), правила совпадают.

const MMSI = /^\d{9}$/

/** Список MMSI → {принято без дублей, отвергнутое}. Разделители — как у сервера. */
export function previewMmsi(text: string): { mmsis: string[]; rejected: string[] } {
  const mmsis: string[] = []
  const rejected: string[] = []
  for (const token of text.split(/[\s,;]+/)) {
    if (!token) continue
    const v = token.endsWith('.0') ? token.slice(0, -2) : token
    if (!MMSI.test(v)) rejected.push(token)
    else if (!mmsis.includes(v)) mmsis.push(v)
  }
  return { mmsis, rejected }
}

/** «2026-10-15» + час → ISO-строка UTC; пустая дата — null. */
export function boundIso(day: string, hour: number): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  return `${day}T${String(hour).padStart(2, '0')}:00:00Z`
}

/** Дата через `days` суток от `day` (для «Сутки», «Неделя»). */
export function addDays(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Начало окна по умолчанию — неделю назад от сегодняшних суток UTC. */
export function defaultFrom(now: Date): string {
  return addDays(now.toISOString().slice(0, 10), -7)
}

export type WindowProblem = 'no_start' | 'end_before_start' | 'too_long' | 'after_data'

/**
 * Что не так с окном, ключом каталога; null — окно годно.
 * `until` — где кончаются данные источника (`available_until` сервера).
 */
export function windowProblem(from: string | null, to: string | null, until: string | null,
                              maxDays: number): WindowProblem | null {
  if (!from) return 'no_start'
  const t0 = Date.parse(from)
  const cap = until ? Date.parse(until) : Infinity
  const t1 = Math.min(to ? Date.parse(to) : cap, cap)
  if (t0 >= cap) return 'after_data'
  if (t1 <= t0) return 'end_before_start'
  if (t1 - t0 > maxDays * 86400_000) return 'too_long'
  return null
}

/** Номер источника отчёта: «Источник 1», «Источник 2», «Источник 3» (файлы,
 *  их привязывает администратор); 0 — общий пропуск по всем. Имени кабинет не знает. */
export type SourceNo = 0 | 1 | 2 | 3
/** Источники, которые ставит «Построить». */
export const SOURCE_NOS: SourceNo[] = [1, 2]
/** Номер общего пропуска. */
export const COMBINED: SourceNo = 0

type Until = { available_until: Partial<Record<SourceNo, string>> }

/**
 * Где кончаются данные хоть одного источника — предел окна формы: отчёт
 * строится по всем источникам, и окно годно, пока его покрывает хоть один.
 */
export function latestUntil(list: Until | undefined): string | null {
  const all = Object.values(list?.available_until ?? {}).filter((v): v is string => !!v)
  return all.length ? all.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b)) : null
}

type RunStatus = 'queued' | 'running' | 'done' | 'failed'
export interface SourceRun { source: SourceNo; id: number; status: RunStatus; t1: string; gaps?: number | null }
export interface Group {
  id: number; title: string | null; t0: string; t1: string; threshold_h: number
  sources: SourceRun[]
}

export type GroupStatus = RunStatus | 'partial'

/** Источники отчёта по номеру (без общего пропуска) и задание общего пропуска. */
export function splitRuns<T extends { source: SourceNo }>(g: { sources: T[] }): { runs: T[]; combined: T | null } {
  const runs = g.sources.filter(s => s.source !== COMBINED).sort((a, b) => a.source - b.source)
  return { runs, combined: g.sources.find(s => s.source === COMBINED) ?? null }
}

/** Статус отчёта: идёт, пока идёт хоть один источник; «частично» — какой-то упал. */
export function groupStatus(g: { sources: { status: RunStatus }[] }): GroupStatus {
  const st = g.sources.map(s => s.status)
  if (st.includes('running')) return 'running'
  if (st.includes('queued')) return st.every(s => s === 'queued') ? 'queued' : 'running'
  if (st.every(s => s === 'done')) return 'done'
  if (st.every(s => s === 'failed')) return 'failed'
  return 'partial'
}

export type TabState =
  | { kind: 'run'; run: SourceRun; cutAt: string | null }
  | { kind: 'no_data' }
  | { kind: 'off' }

/**
 * Что известно об источнике отчёта. Задания нет — окно позже конца данных
 * источника («нет данных») или он был выключен. Окно задания короче отчёта —
 * `cutAt`: данные источника кончаются раньше.
 */
export function sourceTab(g: Group, n: SourceNo, list: Until | undefined): TabState {
  const run = g.sources.find(s => s.source === n)
  if (run) return { kind: 'run', run, cutAt: Date.parse(run.t1) < Date.parse(g.t1) ? run.t1 : null }
  const until = list?.available_until[n]
  return until && Date.parse(g.t0) >= Date.parse(until) ? { kind: 'no_data' } : { kind: 'off' }
}

export interface FormState {
  text: string
  title: string
  from: string
  fromHour: number
  to: string
  toHour: number
  threshold: string
}

/** ISO-момент → [сутки, час] UTC. */
function dayHour(at: string): [string, number] {
  const d = new Date(at)
  return [d.toISOString().slice(0, 10), d.getUTCHours()]
}

/**
 * Настройки отчёта → поля формы, чтобы поставить такой же. Конец окна — самый
 * поздний среди источников (`t1` группы), каким его посчитал сервер, а не «до
 * последних данных»: повтор обязан дать то же окно.
 */
export function formFromGroup(g: { title: string | null; t0: string; t1: string; threshold_h: number },
                              mmsis: string[]): FormState {
  const [from, fromHour] = dayHour(g.t0)
  const [to, toHour] = dayHour(g.t1)
  return { text: mmsis.join('\n'), title: g.title ?? '', from, fromHour, to, toHour,
           threshold: String(g.threshold_h) }
}

/**
 * Доля полосы под сбор треков; остальное — проверка судов рядом с пропусками.
 * Замер прода 28–30.10.2026 (Источник 1, 19 судов): треки ~35 с, соседи 75–95 с.
 */
export const VESSELS_SHARE = 0.3

export interface Progress {
  phase: 'queued' | 'vessels' | 'neighbors'
  /** Заполнение полосы, 0…1. */
  fraction: number
  done: number
  total: number | null
}

/**
 * Общий прогресс задания: суда, затем пропуски, проверенные на соседей. Полоса
 * не откатывается назад, когда число пропусков становится известным: этапы
 * занимают постоянные доли.
 */
export function reportProgress(j: { status: string; done: number; total: number;
                                    neighbors_done?: number | null; neighbors_total?: number | null }): Progress {
  if (j.status === 'queued') return { phase: 'queued', fraction: 0, done: 0, total: null }
  if (j.done < j.total) {
    return { phase: 'vessels', fraction: (VESSELS_SHARE * j.done) / j.total, done: j.done + 1, total: j.total }
  }
  const nt = j.neighbors_total ?? null
  const nd = j.neighbors_done ?? 0
  const part = nt == null ? 0 : nt === 0 ? 1 : Math.min(nd / nt, 1)
  return { phase: 'neighbors', fraction: VESSELS_SHARE + (1 - VESSELS_SHARE) * part, done: nd, total: nt }
}

/** Этап задания словами: ключ каталога и подстановки. */
export function progressLabel(p: Progress): { key: string; params: Record<string, number> } {
  if (p.phase === 'queued') return { key: 'reports.status.queued', params: {} }
  if (p.phase === 'vessels') return { key: 'reports.progress.vessels', params: { done: p.done, total: p.total ?? 0 } }
  return p.total == null ? { key: 'reports.progress.neighbors', params: {} }
    : { key: 'reports.progress.neighborsCount', params: { done: p.done, total: p.total } }
}

/**
 * Прогресс отчёта целиком: доля — среднее по всем его заданиям (готовое — 1,
 * упавшее — 1: ждать его нечего). Общий пропуск ставится последним, поэтому
 * до его появления полоса не доходит до конца.
 */
export function groupProgress(jobs: Parameters<typeof reportProgress>[0][], combinedPending: boolean): number {
  if (!jobs.length) return 0
  const parts = jobs.map(j => (j.status === 'done' || j.status === 'failed' ? 1 : reportProgress(j).fraction))
  const n = parts.length + (combinedPending ? 1 : 0)
  return parts.reduce((a, b) => a + b, 0) / n
}
